/**
 * Get the difference between one Set and another. Rudimentary utility if
 * core-js polyfill cannot be installed. (polyfill required since Set operations
 * are not stable yet as of writing)
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/difference}
 * @param subjectSet - The Set to find elements that are not in otherSet
 * @param otherSet - The Set to check against
 * @returns A new Set containing elements that are in subjectSet but not in otherSet
 */
export function getSetDifference(
  subjectSet: Set<string>,
  otherSet: Set<string>
) {
  const differenceSet = new Set<string>();

  for (const elem of subjectSet) {
    if (!otherSet.has(elem)) {
      differenceSet.add(elem);
    }
  }

  return differenceSet;
}

/**
 * Get the intersection between one Set and another. Rudimentary utility if
 * core-js polyfill cannot be installed. (polyfill required since Set operations
 * are not stable yet as of writing)
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/intersection}
 * @param subjectSet - The Set to find common elements with otherSet
 * @param otherSet - The Set to check against
 * @returns A new Set containing elements that exist in both subjectSet and otherSet
 */
export function getSetIntersection(
  subjectSet: Set<string>,
  otherSet: Set<string>
) {
  const intersectionSet = new Set();

  for (const elem of subjectSet) {
    if (otherSet.has(elem)) {
      intersectionSet.add(elem);
    }
  }

  return intersectionSet;
}

/**
 * Get the union between one Set and another. Rudimentary utility if
 * core-js polyfill cannot be installed. (polyfill required since Set operations
 * are not stable yet as of writing)
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/union}
 * @param subjectSet - The first Set to combine
 * @param otherSet - The second Set to combine
 * @returns A new Set containing all unique elements from both subjectSet and otherSet
 */
export function getSetUnion(subjectSet: Set<string>, otherSet: Set<string>) {
  const unionSet = new Set(subjectSet.values());

  for (const elem of otherSet) {
    unionSet.add(elem);
  }

  return unionSet;
}

/**
 * Determines if one Set is a superset of another. Rudimentary utility if
 * core-js polyfill cannot be installed. (polyfill required since Set operations
 * are not stable yet as of writing). Set A is a superset of Set B if A contains all elements of B.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/isSupersetOf}
 * @param subjectSet - The Set to check if it's a superset
 * @param otherSet - The Set to check against
 * @returns boolean indicating if subjectSet is a superset of otherSet
 */
export function getSetIsSupersetOf(
  subjectSet: Set<string>,
  otherSet: Set<string>
): boolean {
  for (const elem of otherSet) {
    if (!subjectSet.has(elem)) {
      return false;
    }
  }

  return true;
}

/**
 * Determines if one Set is a subset of another. Rudimentary utility if
 * core-js polyfill cannot be installed. (polyfill required since Set operations
 * are not stable yet as of writing). Set A is a subset of Set B if B contains all elements of A.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/isSubsetOf}
 * @param subjectSet - The Set to check if it's a subset
 * @param otherSet - The Set to check against
 * @returns boolean indicating if subjectSet is a subset of otherSet
 */
export function getSetIsSubsetOf(
  subjectSet: Set<string>,
  otherSet: Set<string>
): boolean {
  for (const elem of subjectSet) {
    if (!otherSet.has(elem)) {
      return false;
    }
  }

  return true;
}

/**
 * Determines if one Set is disjoint from another. Rudimentary utility if
 * core-js polyfill cannot be installed. (polyfill required since Set operations
 * are not stable yet as of writing). Two sets are disjoint if they have no elements in common.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/isDisjointFrom}
 * @param subjectSet - The first Set to check for disjointness
 * @param otherSet - The second Set to check against
 * @returns boolean indicating if subjectSet is disjoint from otherSet (have no elements in common)
 */
export function getSetIsDisjointFrom(
  subjectSet: Set<string>,
  otherSet: Set<string>
): boolean {
  for (const elem of subjectSet) {
    if (otherSet.has(elem)) {
      return false;
    }
  }

  return true;
}

/**
 * Get the symmetric difference between one Set and another. Rudimentary utility if
 * core-js polyfill cannot be installed. (polyfill required since Set operations
 * are not stable yet as of writing). Returns elements that are in either set, but not in both.
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set/symmetricDifference}
 * @param subjectSet - The first Set to compare
 * @param otherSet - The second Set to compare
 * @returns A new Set containing elements that are in either subjectSet or otherSet, but not in both
 */
export function getSetSymmetricDifference(
  subjectSet: Set<string>,
  otherSet: Set<string>
): Set<string> {
  const symmetricDifferenceSet = new Set<string>();

  for (const elem of subjectSet) {
    if (!otherSet.has(elem)) {
      symmetricDifferenceSet.add(elem);
    }
  }

  for (const elem of otherSet) {
    if (!subjectSet.has(elem)) {
      symmetricDifferenceSet.add(elem);
    }
  }

  return symmetricDifferenceSet;
}
